import { useMemo, useRef } from 'react';

import { useGetTrackByFrameSeqQuery } from '@/hook';
import { VFrameTrackSlider, new__cn as cn } from '@/package';
import {
  useAuthStore,
  useLabelingBoxStore,
  usePcdCacheStore,
  usePcdFileStore,
  useTaskFrameStore,
} from '@/store';
import type { BoxWithLabel } from '@/type';

import useToolPlayControllerContext from './useToolPlayControllerContext';

export const ToolPlayControllerFrameTrackSlider = () => {
  const {
    allFrameListLength,
    preloadedFrameSeqList,
    isPlaying,
    visibleOnlyMyFrame,
    moveFrameSeq,
    stop,
  } = useToolPlayControllerContext();
  const pcdIndexBoxListMap = useLabelingBoxStore(
    state => state.pcdIndexBoxListMap,
  );
  const selectedBoxIdMap = useLabelingBoxStore(state => state.selectedBoxIdMap);
  const allFrameList = useTaskFrameStore(state => state.allFrameList);
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const pcdDirPath = usePcdFileStore(state => state.path);
  const cacheStatus = usePcdCacheStore(state => state.cacheStatus);

  const selectedBox = useMemo(
    () =>
      useLabelingBoxStore
        .getState()
        .getCurrentSelectedBoxList()[0] as Nullish<BoxWithLabel>,
    [pcdIndexBoxListMap, selectedBoxIdMap],
  );
  const { data: frameList } = useGetTrackByFrameSeqQuery({
    taskID: currentFrame?.taskID,
    trackID: selectedBox?.trackId,
  });

  const frameSeq = currentFrame?.frameSeq ?? 1;
  const cachedRange = useMemo(() => {
    const pcdStatusMap = cacheStatus[pcdDirPath];
    const cachedPcdMap =
      usePcdCacheStore.getState().cachedPcdMapByPath[pcdDirPath];

    if (!pcdStatusMap || !cachedPcdMap) return [];

    const cachedRange: [number, number][] = [];
    let currentRange: [number, number] | null = null;

    Object.entries(cachedPcdMap)
      .map(([pcdIndex, pcdFileName]) => {
        return [+pcdIndex + 1, pcdStatusMap[pcdFileName]] as const;
      })
      .filter(([, status]) => status?.loaded && status?.size > 0)
      .forEach(([frameSeq], index, arr) => {
        const prevSeq = arr[index - 1]?.[0];

        if (prevSeq == null || currentRange == null) {
          currentRange = [frameSeq, frameSeq];
        } else {
          if (frameSeq - 1 === prevSeq) {
            currentRange[1] = frameSeq;
          } else {
            cachedRange.push(currentRange);
            currentRange = [frameSeq, frameSeq];
          }
        }
      });

    if (currentRange) {
      cachedRange.push(currentRange);
    }

    return cachedRange;
  }, [cacheStatus, pcdDirPath]);
  const highlightedRange = useMemo(() => {
    const user = useAuthStore.getState().user;

    if (!visibleOnlyMyFrame || !user) return [];

    const highlightedRange: [number, number][] = [];

    let currentRange: [number, number] | null = null;

    allFrameList.forEach(frame => {
      if (frame && frame.workerID === user.id) {
        if (!currentRange) {
          currentRange = [frame.frameSeq, frame.frameSeq];
        } else {
          currentRange[1] = frame.frameSeq;
        }
      } else {
        if (currentRange) {
          highlightedRange.push(currentRange);
          currentRange = null;
        }
      }
    });

    if (currentRange) {
      highlightedRange.push(currentRange);
    }

    return highlightedRange;
  }, [allFrameList, visibleOnlyMyFrame]);
  const preloadedFrameRangeList = useMemo(() => {
    const preloadedFrameRangeList: [number, number][] = [];
    let currentRange: [number, number] | null = null;

    for (const frameSeq of preloadedFrameSeqList) {
      if (frameSeq == null) {
        if (currentRange) {
          preloadedFrameRangeList.push(currentRange);
          currentRange = null;
        }
      } else {
        if (!currentRange) {
          currentRange = [frameSeq, frameSeq];
        } else {
          currentRange[1] = frameSeq;
        }
      }
    }

    if (currentRange) {
      preloadedFrameRangeList.push(currentRange);
    }

    return preloadedFrameRangeList;
  }, [preloadedFrameSeqList]);
  const trackRangeList = useMemo(() => {
    if (!frameList || !selectedBox) return [];

    const trackRange: [number, number][] = [];
    let currentRange: [number, number] | null = null;

    frameList.forEach((frame, index) => {
      const prevFrame = frameList[index - 1];
      const isValidNextFrame =
        prevFrame && prevFrame.frameSeq === frame.frameSeq - 1;

      if (!isValidNextFrame) {
        if (!currentRange) {
          currentRange = [frame.frameSeq, frame.frameSeq];
        } else {
          trackRange.push(currentRange);
          currentRange = [frame.frameSeq, frame.frameSeq];
        }
      } else if (currentRange && isValidNextFrame) {
        currentRange[1] = frame.frameSeq;
      }
    });

    if (currentRange) {
      trackRange.push(currentRange);
    }

    return trackRange;
  }, [frameList, selectedBox]);

  const $frameBarRef = useRef<HTMLDivElement>(null);

  return allFrameListLength > 0 ? (
    <div
      ref={$frameBarRef}
      aria-label="tool-play-controller-frame-bar"
      className={cn(
        'h-12 border-b border-gray-400 px-2 select-none',
        'flex-1 overflow-x-auto',
      )}
      data-all-frame-list-length={allFrameListLength}
    >
      <VFrameTrackSlider
        cachedRange={cachedRange}
        highlightedRange={
          visibleOnlyMyFrame ? highlightedRange : preloadedFrameRangeList
        }
        max={allFrameListLength}
        min={1}
        preloadedRange={preloadedFrameRangeList}
        trackRange={trackRangeList}
        value={frameSeq}
        onChange={e => {
          if (!isPlaying) stop();

          moveFrameSeq(e.target.value);
        }}
      />
    </div>
  ) : null;
};

ToolPlayControllerFrameTrackSlider.displayName =
  'ToolPlayControllerFrameTrackSlider';
